import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { KnowledgeTestQuestions, QuestionValidationOption } from './knowledge-test';
import { KnowledgeTestFormService } from './knowledge-test-form.service';
import { EnumConverterService } from '../../../services/EnumConverterService';
import { OptionsEnum } from '../../../model/enums/OptionsEnum';
import { ConfirmationDialogComponent } from '../../../dialogs/confirmation-dialog/confirmation-dialog';

type ControlsConfig = { [key: string]: any };
type Page = 1 | 2 | 3;

@Component({
  selector: 'app-knowledge-test',
  templateUrl: './knowledge-test.component.html',
  styleUrls: ['./knowledge-test.component.css'],
})
export class KnowledgeTestComponent implements OnInit, OnDestroy {
  questions = KnowledgeTestQuestions;
  questionsAmount = KnowledgeTestQuestions.length;
  yesNoList = this.enumConverterService.toBaseList('OptionsEnum', OptionsEnum);
  form: FormGroup;
  private readonly MAX_AMOUNT_ERRORS = 1;

  @Input()
  page: Page;

  constructor(
    public knowledgeTestFormService: KnowledgeTestFormService,
    private formBuilder: FormBuilder,
    private enumConverterService: EnumConverterService,
    public dialog: MatDialog,
  ) {
    const controls = this.makeControls();
    this.form = this.formBuilder.group(controls);
  }

  ngOnInit(): void {
    this.setInitialValues();
  }

  ngOnDestroy(): void {
    this.knowledgeTestFormService.setKnowledgeTestFormValues(this.form.value);
  }

  resetKnowledgeTestStateToDefault(): void {
    this.knowledgeTestFormService.setIsTestPassed(false);
    this.knowledgeTestFormService.setIsTestCompleted(false);
  }

  setInitialValues(): void {
    const values = this.knowledgeTestFormService.getKnowledgeTestFormValues();
    if (values) {
      this.form.patchValue(values);
    }
  }

  makeControls(): ControlsConfig {
    const controls: ControlsConfig = {};

    this.questions.forEach(question => {
      controls[question.name] = ['', Validators.required];
    });

    return controls;
  }

  validatePageForNextStep(page: Page): Promise<void> {
    return new Promise((resolve, reject) => {
      const isValid = this.validatePage(page);
      if (isValid) {
        resolve();
      } else {
        reject();
      }
    });
  }

  validatePage(page: Page): boolean {
    let isValid = true;
    const pageQuestions = this.questions.filter(question => question.page === page);

    pageQuestions.forEach(pageQuestion => {
      if (this.form.get(pageQuestion.name).status === 'INVALID') {
        this.form.get(pageQuestion.name).markAsTouched();
        isValid = false;
      }
    });

    return isValid;
  }

  finishTest(): void {
    if (this.knowledgeTestFormService.isTestCompleted) {
      return;
    }
    const confirmation = this.dialog.open(ConfirmationDialogComponent, {
      width: '500px',
      data: {
        confirmationMessage: 'KnowledgeTest.confirmationMessage',
        confirmationMessageTitle: 'KnowledgeTest.confirmationMessageTitle',
        hasFreeText: false,
        optionalButtons: {
          confirmationYES: 'KnowledgeTest.confirmationYESButton',
          confirmationNO: 'KnowledgeTest.confirmationNOButton',
        },
      },
    });

    confirmation.componentInstance.yesEvent.subscribe(() => {
      confirmation.componentInstance.dialogRef.close();
      this.checkTestResults();
    });
  }

  checkTestResults(): void {
    if (!this.form.valid) {
      this.validatePage(3);
      return;
    }
    let errorsAmount = 0;

    this.questions.forEach(question => {
      question.options.forEach(option => {
        if (option.value === this.form.value[question.name] && option.validation === QuestionValidationOption.WRONG) {
          errorsAmount++;
        }
      });
    });

    if (errorsAmount > this.MAX_AMOUNT_ERRORS) {
      this.knowledgeTestFormService.setIsTestPassed(false);
    } else {
      this.knowledgeTestFormService.setIsTestPassed(true);
    }
    this.completeTest();
  }

  completeTest(): void {
    this.knowledgeTestFormService.setIsTestCompleted(true);
  }
}
